// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Filter, Plus, User, Clock, AlertTriangle, CheckCircle2, Eye, FileText, Calendar, BarChart3, LayoutGrid, List, ChevronUp, ChevronDown, ChevronRight, X, PieChart, Users, Settings, Globe, Loader2 } from 'lucide-react';
import CyberSweepAuditDetail from './CyberSweepAuditDetail';
import CyberSweepClientDetail from './CyberSweepClientDetail';
import CyberSweepManagerDashboard from './CyberSweepManagerDashboard';
import AuditTemplatesPage from './AuditTemplatesPage';
import AuditTemplateDetail from './AuditTemplateDetail';
import AuditWebsiteDetail from './AuditWebsiteDetail';
import PAWRecordDetailPage from './PAWRecordDetailPage';
import WebsiteConfigurationPage from './WebsiteConfigurationPage';
import WebsiteConfigurationDetail from './WebsiteConfigurationDetail';
import CyberSweepMyWork from './CyberSweepMyWork';
import CyberSweepTeamDashboard from './CyberSweepTeamDashboard';
import MeetingAgendaTracker from './MeetingAgendaTracker';
import { NewCyberSweepEnrollmentModal } from '../../components/cybersweep/NewCyberSweepEnrollmentModal';
import InternalChatbot from '../../components/common/InternalChatbot';
import { GobodoRequiredGuard } from '../../components/common/GobodoRequiredGuard';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { StandardButton, StandardSearchBar, StandardSelect } from '../../components/standard';
import {
  useCSAudits,
  useCyberSweepClients,
  useCyberSweepClientsInfinite,
  useCyberSweepEnrolledClientsCount,
  useCyberSweepRoadblockedCsAuditCount,
  useAuditorIdToName,
  useCyberSweepListTraversalAuditorNames,
  lookupAuditorDisplayName,
  useCyberSweepBoardColumn,
} from '../../services/queries/cyberSweepQueries';
import { CyberSweepBoardColumn } from '../../components/cybersweep/CyberSweepBoardColumn';
import { useUsers } from '../../services/queries/userQueries';
import { useCyberSweepWorkflowStages } from '../../services/queries/appConfigQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { formatCyberSweepDisplayDate, parseCyberSweepDateToMs } from '../../utils/cyberSweepDisplayDate';
import {
  readCyberSweepClientTraversalString,
  clientRecordsExposesCyberSweepListTraversalFields,
} from '../../utils/cyberSweepClientBoardTraversalFields';

// Environment-specific custom suffix for Gobodo field names (e.g., '__stg5_c' for staging)
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';

interface CyberSweepAppProps {
  onBackToHome?: () => void;
  userRole?: string;
  urlClientId?: string;
  urlAuditId?: string;
  urlTemplateId?: string;
  urlWebsiteId?: string;
  urlView?: 'audit-templates' | 'audit-template-detail' | 'website-configuration' | 'website-configuration-detail';
}

const CyberSweepApp: React.FC<CyberSweepAppProps> = ({
  onBackToHome,
  userRole = 'employee',
  urlClientId,
  urlAuditId,
  urlTemplateId,
  urlWebsiteId,
  urlView,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrentApplication } = useApplicationContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAuditor, setSelectedAuditor] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedAuditWebsiteId, setSelectedAuditWebsiteId] = useState<string | null>(null);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string | null>(null);
  const [selectedPAWId, setSelectedPAWId] = useState<string | null>(null);
  const isManagerRole =
    userRole === 'manager' ||
    userRole === 'system_admin' ||
    userRole === 'cs_supervisor' ||
    userRole === 'cb_supervisor' ||
    userRole === 'cm_supervisor';
  const [currentView, setCurrentView] = useState(() => {
    // If URL has client or audit ID, show appropriate view
    if (urlView === 'audit-template-detail' && urlTemplateId) return 'audit-template-detail';
    if (urlView === 'audit-templates') return 'audit-templates';
    if (urlView === 'website-configuration-detail' && urlWebsiteId) return 'website-configuration-detail';
    if (urlView === 'website-configuration') return 'website-configuration';
    if (urlClientId) return 'client-detail';
    if (urlAuditId) return 'audit-detail';
    // Show manager dashboard for managers and supervisors, operations dashboard for others
    return isManagerRole ? 'manager-dashboard' : 'operations-dashboard';
  });
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Main tab navigation (like Credit Block)
  const [selectedTab, setSelectedTab] = useState<'overview' | 'clients' | 'reports'>('overview');
  // Overview sub-toggle: My Work vs Team Dashboard
  const [overviewMode, setOverviewMode] = useState<'my-work' | 'team-dashboard'>('my-work');

  // Phase date range filters
  const [phase1StartDate, setPhase1StartDate] = useState('');
  const [phase1EndDate, setPhase1EndDate] = useState('');
  const [phase2StartDate, setPhase2StartDate] = useState('');
  const [phase2EndDate, setPhase2EndDate] = useState('');
  const [reportDueDate, setReportDueDate] = useState('');

  // Filter panel state
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // New Enrollment modal state
  const [isNewEnrollmentModalOpen, setIsNewEnrollmentModalOpen] = useState(false);

  // Conditional visibility for Phase date range filters based on Phases dropdown
  const showPhase1DateFilter = selectedPhase?.includes('Phase 1');
  const showPhase2DateFilter = selectedPhase?.includes('Phase 2');

  // Count active filters for badge indicator
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedAuditor) count++;
    if (selectedStage) count++;
    if (selectedPhase) count++;
    if (selectedQuarter) count++;
    if (phase1StartDate || phase1EndDate) count++;
    if (phase2StartDate || phase2EndDate) count++;
    if (reportDueDate) count++;
    return count;
  }, [selectedAuditor, selectedStage, selectedPhase, selectedQuarter, phase1StartDate, phase1EndDate, phase2StartDate, phase2EndDate, reportDueDate]);

  const hasActiveFilters = activeFilterCount > 0;

  // Clear date filters when switching phases to avoid stale filter state
  useEffect(() => {
    // Clear Phase 1 dates when Phase 1 is no longer selected
    if (!showPhase1DateFilter && (phase1StartDate || phase1EndDate)) {
      setPhase1StartDate('');
      setPhase1EndDate('');
    }
    // Clear Phase 2 dates when Phase 2 is no longer selected
    if (!showPhase2DateFilter && (phase2StartDate || phase2EndDate)) {
      setPhase2StartDate('');
      setPhase2EndDate('');
    }
  }, [selectedPhase, showPhase1DateFilter, showPhase2DateFilter]);

  // Sync URL params to state - single source of truth for navigation
  useEffect(() => {
    const defaultView = isManagerRole ? 'manager-dashboard' : 'operations-dashboard';

    if (urlView === 'audit-template-detail' && urlTemplateId) {
      setSelectedTemplateId(urlTemplateId);
      setSelectedWebsiteId(null);
      setSelectedPAWId(null);
      setSelectedAuditWebsiteId(null);
      setCurrentView('audit-template-detail');
    } else if (urlView === 'audit-templates') {
      setSelectedTemplateId(null);
      setSelectedWebsiteId(null);
      setSelectedPAWId(null);
      setSelectedAuditWebsiteId(null);
      setCurrentView('audit-templates');
    } else if (urlView === 'website-configuration-detail' && urlWebsiteId) {
      setSelectedTemplateId(null);
      setSelectedWebsiteId(urlWebsiteId);
      setSelectedPAWId(null);
      setSelectedAuditWebsiteId(null);
      setCurrentView('website-configuration-detail');
    } else if (urlView === 'website-configuration') {
      setSelectedTemplateId(null);
      setSelectedWebsiteId(null);
      setSelectedPAWId(null);
      setSelectedAuditWebsiteId(null);
      setCurrentView('website-configuration');
    } else if (urlClientId) {
      // URL has client ID - set up client detail view
      setSelectedClient((current) => {
        if (current?.clientId === urlClientId) return current;
        return {
          clientName: current?.clientName || '',
          clientId: urlClientId,
          auditId: urlAuditId || current?.auditId || '',
        };
      });
      setCurrentView('client-detail');
    } else if (urlAuditId) {
      // URL has only audit ID - set up audit detail view
      setSelectedAudit(urlAuditId);
      setCurrentView('audit-detail');
    } else {
      // No URL params - reset to dashboard if currently on a detail view
      // This handles browser back button navigation
      setCurrentView((current) => {
        if (
          current === 'client-detail' ||
          current === 'audit-detail' ||
          current === 'audit-templates' ||
          current === 'audit-template-detail'
        ) {
          setSelectedClient(null);
          setSelectedAudit(null);
          setSelectedTemplateId(null);
          setSelectedWebsiteId(null);
          return defaultView;
        }
        return current;
      });
    }
  }, [urlClientId, urlAuditId, urlTemplateId, urlWebsiteId, urlView, isManagerRole]);

  // Set application context for page layout detection
  useEffect(() => {
    console.log('🎯 CyberSweepApp mounted - setting application context to CyberSweep');
    setCurrentApplication('CyberSweep');

    // Reset to Global when unmounting
    return () => {
      console.log('🎯 CyberSweepApp unmounting - resetting application context to Global');
      setCurrentApplication('Global');
    };
  }, [setCurrentApplication]);

  const clientsTabActive = selectedTab === 'clients';
  /** Hooks run before early returns (e.g. client-detail). Without this, Clients tab + board mode stays "active" while a full-page detail view is shown and board/list queries keep competing with the detail page. */
  const mainOpsShellVisible =
    currentView === 'operations-dashboard' || currentView === 'manager-dashboard';
  const clientsDashboardDataEnabled = clientsTabActive && mainOpsShellVisible;

  /** Board: full listed client set (traversal, same as total count). List: paginated traversal (smaller page size). */
  const fullClientsQuery = useCyberSweepClients({
    enabled: USE_GOBODO_API && clientsDashboardDataEnabled && viewMode === 'board',
  });
  const infiniteClientsQuery = useCyberSweepClientsInfinite({
    enabled: USE_GOBODO_API && clientsDashboardDataEnabled && viewMode === 'list',
  });

  /** List infinite query is actually running (not board/overview). */
  const listClientsTraversalQueryActive =
    USE_GOBODO_API && clientsDashboardDataEnabled && viewMode === 'list';

  /**
   * First client traversal has finished (success with pages, or error). When the query is disabled, treat as settled.
   * Do not use `!isFetching` alone — refetches can be idle while data exists.
   */
  const listClientsFirstTraversalSettled = useMemo(() => {
    if (!USE_GOBODO_API || !clientsDashboardDataEnabled || viewMode !== 'list') return true;
    return infiniteClientsQuery.data !== undefined || infiniteClientsQuery.isError;
  }, [clientsDashboardDataEnabled, viewMode, infiniteClientsQuery.data, infiniteClientsQuery.isError]);

  const csClientRecords = useMemo(() => {
    if (viewMode === 'list') {
      return infiniteClientsQuery.data?.pages.flatMap((p) => p.records) ?? [];
    }
    return fullClientsQuery.data ?? [];
  }, [viewMode, infiniteClientsQuery.data, fullClientsQuery.data]);

  /** When true, Client list payloads include CSAudit traversal skin fields — skip legacy `useCSAudits` (list only). */
  const listUsesClientTraversalFields = useMemo(() => {
    if (viewMode !== 'list') return false;
    return clientRecordsExposesCyberSweepListTraversalFields(
      csClientRecords as Record<string, unknown>[],
      CUSTOM_SUFFIX
    );
  }, [viewMode, csClientRecords]);

  /** Gobodo `id` + `key` tokens for merged CSAudit fetch (relationship traversal ∪ field-index union). */
  const clientKeysForCsAudits = useMemo(() => {
    const ids = new Set<string>();
    for (const c of csClientRecords as Record<string, unknown>[]) {
      const id = String(c.id ?? '').trim();
      const key = String(c.key ?? '').trim();
      if (id) ids.add(id);
      if (key) ids.add(key);
    }
    return Array.from(ids);
  }, [csClientRecords]);

  /**
   * List only: first paged client traversal still in flight. Used for paint-first gating — not for CSAudit,
   * template enrichment, or auditor resolution (those must never hide the table).
   * Use `isPending && isFetching` (not `isPending` alone — disabled queries can stay pending with fetchStatus idle).
   */
  const listViewAwaitingFirstClientTraversal =
    listClientsTraversalQueryActive &&
    infiniteClientsQuery.isPending &&
    infiniteClientsQuery.isFetching;

  const boardClientsQueryActive = USE_GOBODO_API && clientsDashboardDataEnabled && viewMode === 'board';

  /**
   * Board: legacy full Client list still used for summary tiles + list-derived grouping — may lag behind kanban columns.
   * List: first client traversal — gates summary until the first page lands (audits still stream in after).
   */
  const loadingClients = viewMode === 'list' ? listViewAwaitingFirstClientTraversal : false;
  const boardSummaryStatsPending =
    boardClientsQueryActive && fullClientsQuery.isPending && fullClientsQuery.isFetching;

  /** After first client rows resolve, wait two animation frames before enabling heavy list joins (paint-first). */
  const [listHeavyQueriesReady, setListHeavyQueriesReady] = useState(false);
  useEffect(() => {
    if (!clientsDashboardDataEnabled || viewMode !== 'list') {
      setListHeavyQueriesReady(false);
      return;
    }
    if (listViewAwaitingFirstClientTraversal) {
      setListHeavyQueriesReady(false);
      return;
    }
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setListHeavyQueriesReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [clientsDashboardDataEnabled, viewMode, listViewAwaitingFirstClientTraversal]);

  /** Skip AppConfig fetch while the first Clients tab query is in flight; defaults cover stage labels until then. */
  const cyberSweepStages = useCyberSweepWorkflowStages({
    enabled: !(USE_GOBODO_API && clientsDashboardDataEnabled && loadingClients),
  });

  /** Total listed clients: `traverseObjectsCount` on the same graph as the client list (start date and/or CS_Audit edge). */
  const enrolledCountQuery = useCyberSweepEnrolledClientsCount({
    enabled:
      USE_GOBODO_API &&
      clientsDashboardDataEnabled &&
      (viewMode === 'board' ||
        (viewMode === 'list' && listClientsFirstTraversalSettled && listHeavyQueriesReady)),
  });

  /** Board: Total Clients uses count endpoint — spin this tile only until count returns (full list may still be paging). */
  const totalClientsTilePending =
    viewMode === 'board' &&
    USE_GOBODO_API &&
    clientsDashboardDataEnabled &&
    enrolledCountQuery.isPending &&
    enrolledCountQuery.isFetching;

  /**
   * List: full CSAudit fetch + template enrichment — does **not** gate table visibility (see `showClientsFullPageLoader`).
   * Enabled after first traversal + post-paint to reduce concurrent Gobodo traffic during first paint.
   */
  const { data: csAuditRecords = [], isFetching: csAuditsFetching } = useCSAudits(undefined, {
    enabled:
      USE_GOBODO_API &&
      clientsDashboardDataEnabled &&
      viewMode === 'list' &&
      listClientsFirstTraversalSettled &&
      listHeavyQueriesReady &&
      !listUsesClientTraversalFields &&
      clientKeysForCsAudits.length > 0,
    clientKeys: clientKeysForCsAudits,
  });
  /**
   * Full-page spinner for list view only while the **client traversal** infinite query is on its first active fetch.
   * Intentionally does not use `useCSAudits` pending state, template enrichment, or auditor lookups — the table
   * renders from `csClientRecords` immediately; audit-derived columns fill in as `csAuditRecords` arrives.
   */
  const showClientsFullPageLoader = viewMode === 'list' && listViewAwaitingFirstClientTraversal;
  /** Client list is enough for summary + list table; audit query only refines per-row audit-derived fields. */
  const showSummaryStats = !loadingClients;

  const boardColumnQueriesEnabled = clientsDashboardDataEnabled && viewMode === 'board';
  const boardSearchQuery = searchQuery.trim();

  /** Zero-hop CSAudit `traverseObjectsCount` on Phase_Status__c roadblock picklist values (optional board search). */
  const roadblockedCountQuery = useCyberSweepRoadblockedCsAuditCount({
    enabled:
      USE_GOBODO_API &&
      clientsDashboardDataEnabled &&
      (viewMode === 'board' ||
        (viewMode === 'list' && listClientsFirstTraversalSettled)),
    searchQuery: viewMode === 'board' ? boardSearchQuery : undefined,
  });

  const roadblockedTilePending =
    viewMode === 'board' &&
    USE_GOBODO_API &&
    clientsDashboardDataEnabled &&
    roadblockedCountQuery.isPending &&
    roadblockedCountQuery.isFetching;

  const bcPendingEnrollments = useCyberSweepBoardColumn('Pending Enrollments', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcLackingRequirements = useCyberSweepBoardColumn('Lacking Requirements', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcAwaitingAssignment = useCyberSweepBoardColumn('Awaiting Assignment', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcPendingChecklists = useCyberSweepBoardColumn('Pending Checklists', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcAwaitingInitialReport = useCyberSweepBoardColumn('Awaiting Initial Report', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcAwaitingFinalReport = useCyberSweepBoardColumn('Awaiting Final Report', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcQuarterlyReport1 = useCyberSweepBoardColumn('Quarterly Report 1', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcQuarterlyReport2 = useCyberSweepBoardColumn('Quarterly Report 2', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcQuarterlyReport3 = useCyberSweepBoardColumn('Quarterly Report 3', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const bcQuarterlyReport4 = useCyberSweepBoardColumn('Quarterly Report 4', {
    enabled: boardColumnQueriesEnabled,
    searchQuery: boardSearchQuery,
  });
  const boardAnyColumnHydrating =
    boardColumnQueriesEnabled &&
    [
      bcPendingEnrollments,
      bcLackingRequirements,
      bcAwaitingAssignment,
      bcPendingChecklists,
      bcAwaitingInitialReport,
      bcAwaitingFinalReport,
      bcQuarterlyReport1,
      bcQuarterlyReport2,
      bcQuarterlyReport3,
      bcQuarterlyReport4,
    ].some((c) => c.isHydratingBoardAudits);
  const { data: auditorIdToNameFromAudits } = useAuditorIdToName(csAuditRecords as Record<string, unknown>[]);

  const listTraversalAuditorNamesQuery = useCyberSweepListTraversalAuditorNames({
    clientRecords: csClientRecords as Record<string, unknown>[],
    enabled:
      USE_GOBODO_API &&
      clientsDashboardDataEnabled &&
      viewMode === 'list' &&
      listClientsFirstTraversalSettled &&
      listHeavyQueriesReady &&
      listUsesClientTraversalFields,
  });

  const auditorIdToName = listUsesClientTraversalFields
    ? (listTraversalAuditorNamesQuery.data ?? {})
    : (auditorIdToNameFromAudits ?? {});

  // Auditor dropdown only on Clients tab; list view loads users after post-paint (same gate as CSAudit batch)
  const usersQueryEnabled =
    listClientsFirstTraversalSettled &&
    clientsDashboardDataEnabled &&
    (viewMode !== 'list' || listHeavyQueriesReady);
  const { data: allSystemUsers = [] } = useUsers({}, { enabled: usersQueryEnabled });

  // Debug: log users for Auditor dropdown
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[CyberSweep] All system users for Auditor dropdown:', allSystemUsers.length, allSystemUsers.slice(0, 5));
    }
  }, [allSystemUsers]);

  const formatDate = (val: unknown): string => formatCyberSweepDisplayDate(val);

  /**
   * Field resolver: checks multiple naming conventions for a given field.
   * RDS sync stores fields with underscores (Phase_Status__c), while code often uses PascalCase (PhaseStatus__c).
   * Also checks camelCase, the env suffix variant, and raw custom__ prefixed names.
   */
  const getField = (r: Record<string, unknown>, ...keys: string[]): unknown => {
    for (const k of keys) {
      if (r[k] !== undefined && r[k] !== null) return r[k];
    }
    return undefined;
  };

  // Mapping of logical field names to actual Gobodo property names (from column-mappings.generated.json)
  // CSAudit fields come from RDS `person_cyber_sweep_aud` table
  const f = (r: Record<string, unknown>, data: Record<string, unknown>, ...keys: string[]): unknown =>
    getField(r, ...keys) ?? getField(data, ...keys);

  const isGobodoLabel = (s: string) => /^(CyberAudit|CSAudit|PAWRecord|Client)-\d+$/i.test(s) || /^a\d[a-z]\d/i.test(s);

  // Strip Gobodo environment suffix from IDs for clean URLs (matches SF ID format)
  const stripSuffix = (s: string) => (s ?? '').replace(/__stg5_c$|__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');

  // Valid board stages that map to columns
  // The Account's Cyber_Sweep_Status__c field already contains the correct stage
  // (set by Salesforce flows based on Audit Template progression)
  const VALID_BOARD_STAGES = [
    'Pending Enrollments',
    'Pending Enrollment',  // Alias (singular)
    'Lacking Requirements',
    'Awaiting Assignment',
    'Pending Checklist',
    'Pending Checklists',  // Alias
    'Awaiting Initial Report',
    'Awaiting Final Report',
    'Quarterly Report 1',
    'Quarterly Report 2',
    'Quarterly Report 3',
    'Quarterly Report 4',
  ];

  // Helper to validate and normalize the board stage from Account's Cyber_Sweep_Status__c
  // Falls back to deriving stage from latest audit template when status is blank
  const getClientBoardStage = (cyberSweepStatus: string, clientName?: string, auditType?: string, hasAudits?: boolean): string => {
    const status = (cyberSweepStatus || '').trim();

    // Debug log for Jane Done
    if (clientName?.toLowerCase().includes('jane')) {
      console.log(`[getClientBoardStage] Input for ${clientName}: status="${status}", auditType="${auditType}", hasAudits=${hasAudits}`);
    }

    // Check if it's already a valid board stage
    if (VALID_BOARD_STAGES.includes(status)) {
      if (clientName?.toLowerCase().includes('jane')) {
        console.log(`[getClientBoardStage] ${clientName}: Valid stage found: "${status}"`);
      }
      // Normalize singular to plural for consistency
      if (status === 'Pending Enrollment') {
        return 'Pending Enrollments';
      }
      return status;
    }

    // Handle common status values that should map to specific columns
    const statusLower = status.toLowerCase();
    if (statusLower.includes('quarterly')) {
      // Extract quarter number if present (e.g., "Q1", "Quarter 1", etc.)
      const match = status.match(/[qQ]?(\d)/);
      if (match) {
        const qNum = parseInt(match[1], 10);
        if (qNum >= 1 && qNum <= 4) return `Quarterly Report ${qNum}`;
      }
    }

    // FALLBACK: When status is blank but client has audits, derive stage from audit template
    if (!status && hasAudits && auditType) {
      const auditTypeLower = auditType.toLowerCase();
      if (clientName?.toLowerCase().includes('jane')) {
        console.log(`[getClientBoardStage] ${clientName}: Status blank, deriving from auditType="${auditType}"`);
      }

      // Map audit template to board stage
      if (auditTypeLower.includes('initial')) {
        return 'Awaiting Initial Report';
      }
      if (auditTypeLower.includes('final')) {
        return 'Awaiting Final Report';
      }
      // Quarterly templates (Q1, Q2, Q3, Q4, Quarterly 1, etc.)
      const qMatch = auditType.match(/[qQ](\d)|[Qq]uarterly\s*(\d)/);
      if (qMatch) {
        const qNum = parseInt(qMatch[1] || qMatch[2], 10);
        if (qNum >= 1 && qNum <= 4) {
          if (clientName?.toLowerCase().includes('jane')) {
            console.log(`[getClientBoardStage] ${clientName}: Derived Quarterly Report ${qNum} from auditType`);
          }
          return `Quarterly Report ${qNum}`;
        }
      }
      // Checklist-related templates
      if (auditTypeLower.includes('checklist')) {
        return 'Pending Checklists';
      }
    }

    // Default fallback for new/pending clients
    if (statusLower === 'pending enrollment' || statusLower === 'pending enrollments') {
      if (clientName?.toLowerCase().includes('jane')) {
        console.log(`[getClientBoardStage] ${clientName}: Mapping to Pending Enrollments (status was: "${status}")`);
      }
      return 'Pending Enrollments';
    }

    // Lacking requirements
    if (statusLower === 'lacking requirements' || statusLower === 'missing requirements') {
      if (clientName?.toLowerCase().includes('jane')) {
        console.log(`[getClientBoardStage] ${clientName}: Mapping to Lacking Requirements (status was: "${status}")`);
      }
      return 'Lacking Requirements';
    }

    // Default fallback for empty status or 'new'
    if (statusLower === 'new' || !status) {
      if (clientName?.toLowerCase().includes('jane')) {
        console.log(`[getClientBoardStage] ${clientName}: Defaulting to Pending Enrollments (status was: "${status}")`);
      }
      return 'Pending Enrollments';
    }

    // Default fallback for unknown statuses
    if (clientName?.toLowerCase().includes('jane')) {
      console.log(`[getClientBoardStage] ${clientName}: Unknown status "${status}", defaulting to Awaiting Assignment`);
    }
    return 'Awaiting Assignment';
  };

  // Build a map of clientId -> audits (sorted by date, latest first)
  // Stores under multiple key variants: raw ID, with suffix, stripped SF ID
  const auditsByClientId = useMemo(() => {
    console.group('[CyberSweep] Building auditsByClientId map');
    console.log('Total csAuditRecords:', csAuditRecords?.length ?? 0);

    // Debug: Show sample audit records with their client reference fields
    if (csAuditRecords.length > 0) {
      const sampleAudits = csAuditRecords.slice(0, 5).map((audit: Record<string, unknown>) => {
        const data = (audit.data as Record<string, unknown>) ?? audit;
        return {
          id: audit.id,
          key: audit.key,
          label: audit.label,
          // All possible client reference fields
          relatedClientId: audit.relatedClientId ?? data.relatedClientId,
          clientId: audit.clientId ?? data.clientId,
          ClientId__c: audit.ClientId__c ?? data.ClientId__c,
          Account__c: audit.Account__c ?? data.Account__c,
          custom__account: audit.custom__account ?? data.custom__account,
        };
      });
      console.log('Sample audit records (client ref fields):', sampleAudits);
    }

    const map: Record<string, Array<Record<string, unknown>>> = {};
    let auditsWithNoClientId = 0;

    for (const audit of csAuditRecords as Record<string, unknown>[]) {
      const data = (audit.data as Record<string, unknown>) ?? audit;
      const clientId = String(
        getField(audit, 'relatedClientId', 'clientId', 'ClientId__c', 'Account__c', 'custom__account') ??
        getField(data, 'relatedClientId', 'clientId', 'ClientId__c', 'Account__c', 'custom__account') ?? ''
      );
      if (!clientId) {
        auditsWithNoClientId++;
        continue;
      }
      // Store under original ID
      if (!map[clientId]) map[clientId] = [];
      map[clientId].push(audit);
      // Also store under stripped SF ID (without suffix) for cross-reference
      const strippedId = clientId.replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');
      if (strippedId !== clientId) {
        if (!map[strippedId]) map[strippedId] = [];
        if (!map[strippedId].includes(audit)) map[strippedId].push(audit);
      }
    }

    console.log('Audits with NO clientId:', auditsWithNoClientId);
    console.log('Unique client IDs in map:', Object.keys(map).length);
    console.log('Sample clientId keys:', Object.keys(map).slice(0, 10));

    // Sort each client's audits by due date (latest first)
    for (const clientId of Object.keys(map)) {
      map[clientId].sort((a, b) => {
        const dataA = (a.data as Record<string, unknown>) ?? a;
        const dataB = (b.data as Record<string, unknown>) ?? b;
        const rawA = getField(a, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? getField(dataA, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date');
        const rawB = getField(b, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? getField(dataB, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date');
        const timeA = parseCyberSweepDateToMs(rawA) ?? 0;
        const timeB = parseCyberSweepDateToMs(rawB) ?? 0;
        return timeB - timeA; // Descending (latest first)
      });
    }

    console.groupEnd();
    return map;
  }, [csAuditRecords]);

  // Map a CLIENT (Account) record to display format, joining with audit data
  const mapClientToDisplay = (client: Record<string, unknown>) => {
    const data = (client.data as Record<string, unknown>) ?? client;
    const clientId = String(client.id ?? client.key ?? '');
    // Get the Gobodo key (SF ID with env suffix) and strip to get clean SF ID
    const clientKey = String(client.key ?? '');
    const sfId = stripSuffix(clientKey); // Clean Salesforce ID for URL routing
    // Strip env suffix to get raw Salesforce ID for audit relationship matching
    const clientKeyRaw = clientKey.replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, '');

    // Client name resolution from client record
    const rawName = String(f(client, data, 'clientName', 'ClientName__c', 'ClientName', 'AccountName__c', 'Name__c', 'Name', 'name') ?? '');
    const recFirstName = String(f(client, data, 'firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name') ?? '');
    const recLastName = String(f(client, data, 'lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name') ?? '');
    const fullNameFromParts = [recFirstName, recLastName].filter(Boolean).join(' ').trim();
    const recordLabel = String(f(client, data, 'label') ?? '');
    const clientName = (rawName && !isGobodoLabel(rawName) ? rawName : '') ||
                       (fullNameFromParts && !isGobodoLabel(fullNameFromParts) ? fullNameFromParts : '') ||
                       (recordLabel && !isGobodoLabel(recordLabel) && !recordLabel.startsWith('Record ') ? recordLabel : '') ||
                       'Unknown Client';
    const parts = clientName.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    // Get Cyber Sweep Status from CLIENT record
    // Check ALL possible field name variants - Gobodo may have data stored under different names
    const cyberSweepStatus = String(f(client, data,
      'cyberSweepStatus', 'Cyber_Sweep_Status__c', 'CyberSweepStatus__c', 'custom__cyber_sweep_status',
      'Custom__cyber_sweep_status__c', `Cyber_Sweep_Status${CUSTOM_SUFFIX}`) ?? '');

    // Get latest audit for this client (if any)
    // Try multiple lookup keys: Gobodo ID, full key with suffix, raw SF ID
    const clientAudits = auditsByClientId[clientId] || auditsByClientId[String(client.key ?? '')] || auditsByClientId[clientKeyRaw] || [];
    const latestAudit = clientAudits[0] as Record<string, unknown> | undefined;
    const latestAuditData = latestAudit ? ((latestAudit.data as Record<string, unknown>) ?? latestAudit) : {};
    const travSuf = CUSTOM_SUFFIX;

    const phaseStatus =
      readCyberSweepClientTraversalString(client, data, 'phaseStatus', travSuf) ||
      (latestAudit ? String(f(latestAudit, latestAuditData,
        'phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status') ?? '') : '');
    const auditTypeFromTrav = readCyberSweepClientTraversalString(client, data, 'auditType', travSuf);
    const auditType = auditTypeFromTrav
      ? auditTypeFromTrav
      : latestAudit ? (() => {
      const candidates = [
        f(latestAudit, latestAuditData, 'auditTypeDisplayName', 'AuditTypeDisplayName__c'),
        f(latestAudit, latestAuditData, 'Audit_Type_Name__c', 'auditTypeName'),
        f(latestAudit, latestAuditData, 'auditTemplateName', 'AuditTemplateName__c'),
        f(latestAudit, latestAuditData, 'auditType', 'Audit_Type__c', 'AuditType__c', 'custom__audit_type'),
      ];
      const isNotSfId = (v: unknown) => {
        const s = String(v || '').trim();
        if (!s) return false;
        if (/^[a-zA-Z0-9]{15,18}$/.test(s) && /^[a-z][0-9][A-Za-z]/.test(s)) return false;
        return true;
      };
      return String(candidates.find(isNotSfId) ?? '').trim();
    })() : '';
    const roadblock =
      readCyberSweepClientTraversalString(client, data, 'roadblock', travSuf) ||
      (latestAudit ? String(f(latestAudit, latestAuditData,
        'roadBlockNote', 'Road_Block_Note__c', 'RoadBlockNote__c', 'custom__road_block_note') ?? '') : '');
    const rawDueDate =
      readCyberSweepClientTraversalString(client, data, 'dueDate', travSuf) ||
      (latestAudit ? (f(latestAudit, latestAuditData, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? '') : '');
    const auditId =
      readCyberSweepClientTraversalString(client, data, 'auditKey', travSuf) ||
      (latestAudit ? String(latestAudit.id ?? latestAudit.key ?? '') : '');

    const auditorRaw =
      readCyberSweepClientTraversalString(client, data, 'auditor', travSuf) ||
      (latestAudit ? String(
        latestAuditData.Auditor__c ?? latestAuditData.auditor ?? latestAuditData.custom__auditor ??
        latestAudit.Auditor__c ?? latestAudit.auditor ?? latestAudit.custom__auditor ?? ''
      ).trim() : '');
    const auditor = auditorRaw ? (lookupAuditorDisplayName(auditorIdToName, auditorRaw) || auditorRaw) : '';

    // Determine board column from Account's Cyber_Sweep_Status__c field
    // The status field already contains the correct board stage (including Quarterly Report 1-4)
    // This is set by Salesforce flows based on Audit Template progression
    // FALLBACK: When status is blank but client has audits, derive from latest audit template
    const stage = getClientBoardStage(
      cyberSweepStatus,
      clientName,
      auditType,
      clientAudits.length > 0 || Boolean(String(auditId ?? '').trim())
    );

    const rawPhase1Start =
      readCyberSweepClientTraversalString(client, data, 'phase1Start', travSuf) ||
      (latestAudit ? (f(latestAudit, latestAuditData,
        'phase1StartDateTime', 'Phase_1_Start_Date_Time__c', 'Phase1StartDateTime__c',
        'Phase_1_Start_Date__c', 'custom__phase_1_start_date_time') ?? '') : '');
    const rawPhase2Start =
      readCyberSweepClientTraversalString(client, data, 'phase2Start', travSuf) ||
      (latestAudit ? (f(latestAudit, latestAuditData,
        'phase2StartDateTime', 'Phase_2_Start_Date_Time__c', 'Phase2StartDateTime__c',
        'Phase_2_Start_Date__c', 'custom__phase_2_start_date_time') ?? '') : '');
    const rawEnrolled = f(client, data, 'createdDate', 'CreatedDate__c', 'created_date',
      'cyberSweepStartDate', 'CyberSweep_Start_Date__c', 'custom__cyber_sweep_start_date') ?? '';

    return {
      id: clientId,  // CLIENT id is the primary identifier (Gobodo numeric ID)
      clientId,
      sfId,          // Clean Salesforce ID for URL routing (e.g., 001Nq00000J7N4z)
      firstName,
      lastName,
      clientName,
      auditId,       // Latest audit ID (for navigation)
      stage,
      phaseStatus,   // From latest audit
      auditType,     // From latest audit
      roadblock,     // From latest audit
      auditor,       // From latest audit
      dueDate: formatDate(rawDueDate),
      dueDateRaw: String(rawDueDate),
      enrolledDate: formatDate(rawEnrolled),
      quarter:
        readCyberSweepClientTraversalString(client, data, 'quarter', travSuf) ||
        (latestAudit ? String(f(latestAudit, latestAuditData, 'quarter', 'Quarter__c') ?? '') : ''),
      year:
        readCyberSweepClientTraversalString(client, data, 'fiscalYear', travSuf) ||
        (latestAudit ? String(f(latestAudit, latestAuditData, 'fiscalYear', 'Fiscal_Year__c', 'FiscalYear__c', 'custom__fiscal_year') ?? '') : ''),
      phase1StartDate: String(rawPhase1Start),
      phase2StartDate: String(rawPhase2Start),
      auditCount: clientAudits.length || (String(auditId ?? '').trim() ? 1 : 0),
    };
  };

  // displayRecords: Map CLIENT records (those with CyberSweep Start Date) joined with audit info
  // Each client appears exactly once
  const displayRecords = useMemo(() => {
    if (!USE_GOBODO_API) return [];

    // Map each CyberSweep client to a display record
    const mapped = (csClientRecords as Record<string, unknown>[]).map(mapClientToDisplay);

    if (process.env.NODE_ENV === 'development') {
      console.group('[CyberSweep Debug] Client-based displayRecords');
      console.log(`Total CyberSweep clients: ${csClientRecords.length}, Total audits: ${csAuditRecords.length}`);
      console.log('Stage distribution:', mapped.reduce((acc, r) => { acc[r.stage] = (acc[r.stage] || 0) + 1; return acc; }, {} as Record<string, number>));

      // Find clients with 0 audits
      const clientsWithNoAudits = mapped.filter(r => r.auditCount === 0);
      console.log(`Clients with 0 audits: ${clientsWithNoAudits.length}`, clientsWithNoAudits.map(c => c.clientName));

      // Find clients with audits
      const clientsWithAudits = mapped.filter(r => r.auditCount > 0);
      console.log(`Clients WITH audits: ${clientsWithAudits.length}`, clientsWithAudits.map(c => ({ name: c.clientName, count: c.auditCount })));

      // Specifically look for Jane Done to debug stage assignment
      const janeDone = mapped.find(r => r.clientName?.toLowerCase().includes('jane') && r.clientName?.toLowerCase().includes('done'));
      if (janeDone) {
        console.group('[DEBUG] Jane Done Analysis');
        console.log('Mapped record:', janeDone);
        console.log('Stage assigned:', janeDone.stage);
        console.log('Audit count:', janeDone.auditCount);
        console.log('Phase status (from audit):', janeDone.phaseStatus);

        // Find Jane Done in RAW client records to see all field names
        const janeRawClient = (csClientRecords as Record<string, unknown>[]).find((c: Record<string, unknown>) => {
          const data = (c.data as Record<string, unknown>) ?? c;
          const name = String(c.Name ?? c.name ?? data.Name ?? data.name ?? c.label ?? '').toLowerCase();
          return name.includes('jane') && name.includes('done');
        });
        if (janeRawClient) {
          const janeData = (janeRawClient.data as Record<string, unknown>) ?? janeRawClient;
          console.log('RAW Account field names:', Object.keys(janeRawClient).sort());
          console.log('RAW data field names:', Object.keys(janeData).sort());
          // Check all possible cyberSweepStatus field names
          console.log('Cyber_Sweep_Status__c:', janeRawClient.Cyber_Sweep_Status__c ?? janeData.Cyber_Sweep_Status__c);
          console.log('CyberSweepStatus__c:', janeRawClient.CyberSweepStatus__c ?? janeData.CyberSweepStatus__c);
          console.log('cyberSweepStatus:', janeRawClient.cyberSweepStatus ?? janeData.cyberSweepStatus);
          console.log('custom__cyber_sweep_status:', janeRawClient.custom__cyber_sweep_status ?? janeData.custom__cyber_sweep_status);
          // Check with env suffix
          const suffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX || '__c';
          console.log(`Cyber_Sweep_Status${suffix}:`, janeRawClient[`Cyber_Sweep_Status${suffix}`] ?? janeData[`Cyber_Sweep_Status${suffix}`]);
          // Find all fields containing 'status'
          const statusFields = [...Object.keys(janeRawClient), ...Object.keys(janeData)]
            .filter(k => k.toLowerCase().includes('status'));
          console.log('All status-related fields:', statusFields);
        }

        // Check Jane's audits
        const janeAudits = auditsByClientId[janeDone.clientId] || [];
        console.log('Jane audits count:', janeAudits.length);
        if (janeAudits.length > 0) {
          const latestAudit = janeAudits[0] as Record<string, unknown>;
          const auditData = (latestAudit.data as Record<string, unknown>) ?? latestAudit;
          console.log('Latest audit:', {
            id: latestAudit.id,
            phaseStatus: latestAudit.Phase_Status__c ?? auditData.Phase_Status__c ?? latestAudit.phaseStatus,
            auditType: latestAudit.Audit_Type__c ?? auditData.Audit_Type__c,
            dueDate: latestAudit.Due_Date__c ?? auditData.Due_Date__c,
          });
        }
        console.groupEnd();
      } else {
        console.warn('[DEBUG] Jane Done NOT FOUND in mapped clients');
        // Try to find in raw client records
        const janeRaw = (csClientRecords as Record<string, unknown>[]).find((c: Record<string, unknown>) => {
          const data = (c.data as Record<string, unknown>) ?? c;
          const name = String(c.Name ?? c.name ?? data.Name ?? data.name ?? c.label ?? '').toLowerCase();
          return name.includes('jane') && name.includes('done');
        });
        if (janeRaw) {
          console.log('[DEBUG] Jane Done in RAW records:', { id: janeRaw.id, key: janeRaw.key, label: janeRaw.label });
        }
      }

      // Show sample client IDs for comparison with audit clientIds
      console.log('Sample client IDs:', (csClientRecords as Record<string, unknown>[]).slice(0, 5).map((c: Record<string, unknown>) => ({
        id: c.id,
        key: c.key,
        strippedKey: String(c.key ?? '').replace(/(__stg3_c|__stg_c|__dev_c|__c)$/i, ''),
      })));

      console.groupEnd();
    }
    return mapped;
  }, [csClientRecords, csAuditRecords, auditsByClientId, auditorIdToName]);

  /** Total from `traverseObjectsCount` when available; otherwise loaded row count (fallback / non-Gobodo). */
  const totalClientsForSummary = useMemo(() => {
    if (enrolledCountQuery.data != null) {
      return enrolledCountQuery.data;
    }
    return displayRecords.length;
  }, [enrolledCountQuery.data, displayRecords.length]);

  // Build auditor options from all system users (User Lookup)
  const auditorOptions = useMemo(() => {
    const options = allSystemUsers
      .map(user => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`.trim() || user.email
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (process.env.NODE_ENV === 'development') {
      console.log('[CyberSweep] auditorOptions:', options.length, options.slice(0, 5));
    }
    return options;
  }, [allSystemUsers]);

  // Legacy: auditors extracted from existing records (used for filter matching)
  const auditors = useMemo(() => {
    const set = new Set<string>();
    displayRecords.forEach(r => r.auditor && set.add(r.auditor));
    return Array.from(set).sort();
  }, [displayRecords]);

  /** Raw Phase_Status__c values from data (Salesforce picklist), for list/board filter — distinct from derived workflow Stage. */
  const phaseStatuses = useMemo(() => {
    const set = new Set<string>();
    displayRecords.forEach((r) => {
      const p = (r.phaseStatus || '').trim();
      if (p) set.add(p);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [displayRecords]);

  const byStage = useMemo(() => {
    const groups: Record<string, typeof displayRecords> = {};
    cyberSweepStages.forEach(s => { groups[s] = []; });
    displayRecords.forEach(r => {
      if (!groups[r.stage]) groups[r.stage] = [];
      groups[r.stage].push(r);
    });
    return groups;
  }, [displayRecords, cyberSweepStages]);

  const pendingEnrollments = byStage['Pending Enrollments'] || [];
  const lackingRequirements = byStage['Lacking Requirements'] || [];
  const awaitingAssignment = byStage['Awaiting Assignment'] || [];

  /** Fallback when `traverseObjectsCount` on CSAudit Phase_Status is unavailable; matches substring "road"+"block". */
  const roadblockedClients = useMemo(() => {
    return displayRecords.filter(r => {
      const ps = (r.phaseStatus || '').toLowerCase();
      return ps.includes('road') && ps.includes('block');
    });
  }, [displayRecords]);

  const roadblockedCountForSummary = useMemo(() => {
    if (roadblockedCountQuery.data != null) {
      return roadblockedCountQuery.data;
    }
    return roadblockedClients.length;
  }, [roadblockedCountQuery.data, roadblockedClients.length]);
  const pendingChecklists = byStage['Pending Checklists'] || [];
  const awaitingInitialReport = byStage['Awaiting Initial Report'] || [];
  const awaitingFinalReport = byStage['Awaiting Final Report'] || [];
  const quarterlyReport1 = byStage['Quarterly Report 1'] || [];
  const quarterlyReport2 = byStage['Quarterly Report 2'] || [];
  const quarterlyReport3 = byStage['Quarterly Report 3'] || [];
  const quarterlyReport4 = byStage['Quarterly Report 4'] || [];

  const allClients = displayRecords;

  const getPhaseStatusColor = (ps: string) => {
    const lower = ps.toLowerCase();
    if (lower.includes('completed') || lower.includes('done')) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (lower.includes('in progress')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    if (lower.includes('road') || lower.includes('block')) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    if (lower.includes('review')) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  // Filter clients by stage, phase status (SF picklist), quarter, auditor, and search
  const getFilteredClients = (clients: any[]) => {
    return clients.filter(client => {
      // Stage filter
      if (selectedStage && client.stage !== selectedStage) return false;

      // Phase status filter (matches Phase_Status__c / list column)
      if (selectedPhase && (client.phaseStatus || '').trim() !== selectedPhase) return false;

      // Quarter filter (for quarterly reports)
      if (selectedQuarter && client.quarter && client.quarter !== selectedQuarter) return false;

      // Auditor filter - match by user ID or display name
      if (selectedAuditor) {
        // Find the selected auditor's display name from the options
        const selectedAuditorOption = auditorOptions.find(opt => opt.value === selectedAuditor);
        const selectedAuditorName = selectedAuditorOption?.label || selectedAuditor;
        // Match if client's auditor matches the selected name (case-insensitive)
        const clientAuditor = (client.auditor || '').toLowerCase().trim();
        const filterName = selectedAuditorName.toLowerCase().trim();
        if (clientAuditor !== filterName && client.auditor !== selectedAuditor) return false;
      }

      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const firstName = client.firstName?.toLowerCase() || '';
        const lastName = client.lastName?.toLowerCase() || '';
        const clientName = client.clientName?.toLowerCase() || '';
        const auditId = client.auditId?.toLowerCase() || '';

        if (
          !firstName.includes(searchLower) &&
          !lastName.includes(searchLower) &&
          !clientName.includes(searchLower) &&
          !auditId.includes(searchLower)
        ) return false;
      }

      // Phase 1 date range filter
      if (phase1StartDate || phase1EndDate) {
        const clientPhase1Date = client.phase1StartDate ? new Date(client.phase1StartDate) : null;
        if (clientPhase1Date && !isNaN(clientPhase1Date.getTime())) {
          if (phase1StartDate && clientPhase1Date < new Date(phase1StartDate)) return false;
          if (phase1EndDate && clientPhase1Date > new Date(phase1EndDate + 'T23:59:59')) return false;
        } else if (phase1StartDate || phase1EndDate) {
          // If filter is set but client has no phase 1 date, exclude it
          return false;
        }
      }

      // Phase 2 date range filter
      if (phase2StartDate || phase2EndDate) {
        const clientPhase2Date = client.phase2StartDate ? new Date(client.phase2StartDate) : null;
        if (clientPhase2Date && !isNaN(clientPhase2Date.getTime())) {
          if (phase2StartDate && clientPhase2Date < new Date(phase2StartDate)) return false;
          if (phase2EndDate && clientPhase2Date > new Date(phase2EndDate + 'T23:59:59')) return false;
        } else if (phase2StartDate || phase2EndDate) {
          // If filter is set but client has no phase 2 date, exclude it
          return false;
        }
      }

      // Report Due Date filter
      if (reportDueDate) {
        const dueMs = client.dueDateRaw ? parseCyberSweepDateToMs(client.dueDateRaw) : null;
        const clientDueDate = dueMs != null ? new Date(dueMs) : null;
        if (clientDueDate && !isNaN(clientDueDate.getTime())) {
          // Match if due date is on or before the selected date
          if (clientDueDate > new Date(reportDueDate + 'T23:59:59')) return false;
        } else {
          // If filter is set but client has no due date, exclude it
          return false;
        }
      }

      return true;
    });
  };

  // Apply filters to all client lists
  const filteredPendingEnrollments = getFilteredClients(pendingEnrollments);
  const filteredLackingRequirements = getFilteredClients(lackingRequirements);
  const filteredAwaitingAssignment = getFilteredClients(awaitingAssignment);
  const filteredPendingChecklists = getFilteredClients(pendingChecklists);
  const filteredAwaitingInitialReport = getFilteredClients(awaitingInitialReport);
  const filteredAwaitingFinalReport = getFilteredClients(awaitingFinalReport);
  const filteredQuarterlyReport1 = getFilteredClients(quarterlyReport1);
  const filteredQuarterlyReport2 = getFilteredClients(quarterlyReport2);
  const filteredQuarterlyReport3 = getFilteredClients(quarterlyReport3);
  const filteredQuarterlyReport4 = getFilteredClients(quarterlyReport4);

  const filteredBoardPendingEnrollments = getFilteredClients(
    (bcPendingEnrollments.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardLackingRequirements = getFilteredClients(
    (bcLackingRequirements.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardAwaitingAssignment = getFilteredClients(
    (bcAwaitingAssignment.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardPendingChecklists = getFilteredClients(
    (bcPendingChecklists.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardAwaitingInitialReport = getFilteredClients(
    (bcAwaitingInitialReport.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardAwaitingFinalReport = getFilteredClients(
    (bcAwaitingFinalReport.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardQuarterlyReport1 = getFilteredClients(
    (bcQuarterlyReport1.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardQuarterlyReport2 = getFilteredClients(
    (bcQuarterlyReport2.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardQuarterlyReport3 = getFilteredClients(
    (bcQuarterlyReport3.data?.pages ?? []).flatMap((p) => p.rows)
  );
  const filteredBoardQuarterlyReport4 = getFilteredClients(
    (bcQuarterlyReport4.data?.pages ?? []).flatMap((p) => p.rows)
  );

  // Filtered all clients for list view (applies same filters as board view)
  const filteredAllClients = useMemo(() => {
    return getFilteredClients(allClients);
  }, [allClients, selectedStage, selectedPhase, selectedQuarter, selectedAuditor, searchQuery, auditorOptions, phase1StartDate, phase1EndDate, phase2StartDate, phase2EndDate, reportDueDate]);

  // Sorted clients for list view
  const sortedAllClients = useMemo(() => {
    if (!sortConfig) return filteredAllClients;

    return [...filteredAllClients].sort((a, b) => {
      let aValue: any = a[sortConfig.key as keyof typeof a];
      let bValue: any = b[sortConfig.key as keyof typeof b];

      // Handle nested or computed values
      if (sortConfig.key === 'clientName') {
        aValue = a.clientName || `${a.firstName} ${a.lastName}`;
        bValue = b.clientName || `${b.firstName} ${b.lastName}`;
      }

      // Handle null/undefined
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Number comparison
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAllClients, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        // Toggle direction or clear
        if (current.direction === 'asc') return { key, direction: 'desc' };
        if (current.direction === 'desc') return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-4 w-4 inline ml-1" />
      : <ChevronDown className="h-4 w-4 inline ml-1" />;
  };

  const handleClientClick = (clientName: string, auditId: string, sfId?: string) => {
    // sfId should be the clean Salesforce ID (e.g., 001Nq00000J7N4z)
    const urlClientId = sfId || stripSuffix(auditId);
    void queryClient.cancelQueries({ queryKey: ['cybersweep', 'board-column'] });
    void queryClient.cancelQueries({ queryKey: ['cybersweep', 'board-column-audits'] });
    void queryClient.cancelQueries({ queryKey: ['cybersweep', 'clients'] });
    setSelectedClient({ clientName, clientId: urlClientId, auditId });
    setCurrentView('client-detail');
    // Update URL for shareability - use SF ID for clean URLs
    // Don't use replace: true so browser back button works correctly
    const urlAuditId = stripSuffix(auditId);
    if (urlAuditId && urlAuditId !== urlClientId) {
      navigate(`/cybersweep/client/${urlClientId}/audit/${urlAuditId}`);
    } else {
      navigate(`/cybersweep/client/${urlClientId}`);
    }
  };

  const handleViewAudit = (auditId: string) => {
    console.log('[handleViewAudit] Called with auditId:', auditId);
    console.log('[handleViewAudit] selectedClient:', selectedClient);
    setSelectedAudit(auditId);
    setCurrentView('audit-detail');
    // Include client ID in URL when viewing audit from client context
    // This allows page refresh to stay on the audit instead of going back to client
    // Don't use replace: true so browser back button works correctly
    if (selectedClient?.clientId) {
      const newUrl = `/cybersweep/client/${stripSuffix(selectedClient.clientId)}/audit/${stripSuffix(auditId)}`;
      console.log('[handleViewAudit] Navigating to:', newUrl);
      navigate(newUrl);
    } else {
      const newUrl = `/cybersweep/audit/${stripSuffix(auditId)}`;
      console.log('[handleViewAudit] Navigating to (no client):', newUrl);
      navigate(newUrl);
    }
  };

  const handleViewOperations = () => {
    setCurrentView('operations-dashboard');
  };

  const handleViewManagerDashboard = () => {
    setCurrentView('manager-dashboard');
  };

  const wrapWithGuard = (children: React.ReactNode) => <GobodoRequiredGuard>{children}</GobodoRequiredGuard>;

  if (currentView === 'manager-dashboard') {
    return wrapWithGuard(
      <>
        <CyberSweepManagerDashboard onViewOperations={handleViewOperations} />
        <InternalChatbot applicationContext="cybersweep" position="bottom-right" />
      </>
    );
  }

  if (currentView === 'client-detail' && selectedClient) {
    return wrapWithGuard(
      <>
        <CyberSweepClientDetail
          clientId={selectedClient.clientId}
          auditId={selectedClient.auditId}
          onBack={() => {
            setCurrentView(isManagerRole ? 'manager-dashboard' : 'operations-dashboard');
            setSelectedClient(null);
            // Reset URL to base CyberSweep
            navigate('/cybersweep', { replace: true });
          }}
          onViewAudit={handleViewAudit}
        />
        <InternalChatbot
          applicationContext="cybersweep"
          clientContext={{ clientId: selectedClient.clientId, auditId: selectedClient.auditId }}
          position="bottom-right"
        />
      </>
    );
  }

  if (currentView === 'audit-detail' && selectedAudit) {
    return wrapWithGuard(
      <>
        <CyberSweepAuditDetail
          auditId={selectedAudit}
          onBack={() => {
            if (selectedClient) {
              setCurrentView('client-detail');
              setSelectedAudit(null);
              // URL goes back to client - use stripped SF ID
              navigate(`/cybersweep/client/${stripSuffix(selectedClient.clientId)}`, { replace: true });
            } else {
              setCurrentView(isManagerRole ? 'manager-dashboard' : 'operations-dashboard');
              setSelectedAudit(null);
              // Reset URL to base CyberSweep
              navigate('/cybersweep', { replace: true });
            }
          }}
        />
        <InternalChatbot applicationContext="cybersweep" clientContext={{ auditId: selectedAudit }} position="bottom-right" />
      </>
    );
  }

  // Audit Template Detail Page (editable)
  if (currentView === 'audit-template-detail' && selectedTemplateId) {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <AuditTemplateDetail
            templateId={selectedTemplateId}
            onBack={() => {
              setSelectedTemplateId(null);
              navigate('/cybersweep/audit-templates');
            }}
            onAuditWebsiteClick={(websiteId: string) => {
              setSelectedAuditWebsiteId(websiteId);
              setCurrentView('audit-website-detail');
            }}
            onPAWClick={(pawId: string, csAuditId?: string) => {
              // Navigate to PAW detail page
              setSelectedPAWId(pawId);
              setCurrentView('paw-record-detail');
            }}
          />
        </div>
      </div>
    );
  }

  // Audit Website Detail Page (CSAW records)
  if (currentView === 'audit-website-detail' && selectedAuditWebsiteId) {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <AuditWebsiteDetail
            websiteId={selectedAuditWebsiteId}
            onBack={() => {
              setSelectedAuditWebsiteId(null);
              // Go back to template detail if we came from there
              if (selectedTemplateId) {
                setCurrentView('audit-template-detail');
              } else {
                setCurrentView('audit-templates');
              }
            }}
            onTemplateClick={(templateId: string) => {
              setSelectedTemplateId(templateId);
              setCurrentView('audit-template-detail');
            }}
          />
        </div>
      </div>
    );
  }

  // PAW Record Detail Page
  if (currentView === 'paw-record-detail' && selectedPAWId) {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <PAWRecordDetailPage
            pawId={selectedPAWId}
            onBack={() => {
              setSelectedPAWId(null);
              // Go back to template detail if we came from there
              if (selectedTemplateId) {
                setCurrentView('audit-template-detail');
              } else {
                setCurrentView('audit-templates');
              }
            }}
            onAuditClick={(auditId: string) => {
              setSelectedAudit(auditId);
              setCurrentView('audit-detail');
            }}
          />
        </div>
      </div>
    );
  }

  // Website Configuration Detail Page (editable)
  if (currentView === 'website-configuration-detail' && selectedWebsiteId) {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <WebsiteConfigurationDetail
            websiteId={selectedWebsiteId}
            onBack={() => {
              setSelectedWebsiteId(null);
              navigate('/cybersweep/website-configuration');
            }}
          />
        </div>
      </div>
    );
  }

  // Meeting Agenda & Tracker Page
  if (currentView === 'meeting-tracker') {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <MeetingAgendaTracker
            onBack={() => {
              setCurrentView('operations-dashboard');
              setSelectedTab('reports');
            }}
          />
        </div>
      </div>
    );
  }

  // Audit Templates Configuration Page
  if (currentView === 'audit-templates') {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">CyberSweep Operations</h1>
              </div>
              <div className="flex gap-3 shrink-0">
                <StandardButton
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsNewEnrollmentModalOpen(true)}
                >
                  New Enrollment
                </StandardButton>
              </div>
            </div>
          </div>

          {/* Main Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-600">
              <nav className="flex space-x-8">
                <button
                  onClick={() => { setSelectedTab('overview'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>Overview</span>
                </button>
                <button
                  onClick={() => { setSelectedTab('clients'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <Users className="h-4 w-4" />
                  <span>Clients</span>
                </button>
                <button
                  onClick={() => { setSelectedTab('reports'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <PieChart className="h-4 w-4" />
                  <span>Reports</span>
                </button>

                {/* Configuration Dropdown - Right aligned, Active state */}
                <div className="relative group ml-auto">
                  <button
                    className="flex items-center space-x-2 py-4 px-1 border-b-2 border-blue-500 font-medium text-sm text-blue-600 dark:text-blue-400"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Configuration</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="py-1">
                      <button
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20"
                      >
                        <FileText className="h-4 w-4" />
                        Audit Templates
                      </button>
                      <button
                        onClick={() => navigate('/cybersweep/website-configuration')}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <Globe className="h-4 w-4" />
                        Website Configuration
                      </button>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </div>

          {/* Audit Templates Content */}
          <AuditTemplatesPage
            onBack={() => navigate('/cybersweep')}
            onTemplateClick={(templateId) => {
              setSelectedTemplateId(templateId);
              setSelectedPAWId(null);
              setSelectedAuditWebsiteId(null);
              setCurrentView('audit-template-detail');
              navigate(`/cybersweep/audit-templates/${stripSuffix(templateId)}`);
            }}
          />
        </div>
        <InternalChatbot applicationContext="cybersweep" position="bottom-right" />
      </div>
    );
  }

  // Website Configuration Page
  if (currentView === 'website-configuration') {
    return wrapWithGuard(
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">CyberSweep Operations</h1>
              </div>
              <div className="flex gap-3 shrink-0">
                <StandardButton
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsNewEnrollmentModalOpen(true)}
                >
                  New Enrollment
                </StandardButton>
              </div>
            </div>
          </div>

          {/* Main Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-600">
              <nav className="flex space-x-8">
                <button
                  onClick={() => { setSelectedTab('overview'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>Overview</span>
                </button>
                <button
                  onClick={() => { setSelectedTab('clients'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <Users className="h-4 w-4" />
                  <span>Clients</span>
                </button>
                <button
                  onClick={() => { setSelectedTab('reports'); setCurrentView('operations-dashboard'); }}
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <PieChart className="h-4 w-4" />
                  <span>Reports</span>
                </button>
                {/* Configuration Dropdown - Right aligned, Active state */}
                <div className="relative group ml-auto">
                  <button
                    className="flex items-center space-x-2 py-4 px-1 border-b-2 border-blue-500 font-medium text-sm text-blue-600 dark:text-blue-400"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Configuration</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="py-1">
                      <button
                        onClick={() => navigate('/cybersweep/audit-templates')}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FileText className="h-4 w-4" />
                        Audit Templates
                      </button>
                      <button
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20"
                      >
                        <Globe className="h-4 w-4" />
                        Website Configuration
                      </button>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </div>

          {/* Website Configuration Content */}
          <WebsiteConfigurationPage
            onBack={() => navigate('/cybersweep')}
            onWebsiteClick={(websiteId) => {
              setSelectedWebsiteId(websiteId);
              navigate(`/cybersweep/website-configuration/${websiteId}`);
            }}
          />
        </div>
        <InternalChatbot applicationContext="cybersweep" position="bottom-right" />
      </div>
    );
  }

  return wrapWithGuard(
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">CyberSweep Operations</h1>
            </div>
            <div className="flex gap-3 shrink-0">
              <StandardButton
                variant="primary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setIsNewEnrollmentModalOpen(true)}
              >
                New Enrollment
              </StandardButton>
            </div>
          </div>
        </div>

        {/* Main Tab Navigation (like Credit Block) */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-600">
            <nav className="flex space-x-8">
              {/* Overview Tab */}
              <button
                onClick={() => setSelectedTab('overview')}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'overview'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="h-4 w-4" />
                <span>Overview</span>
              </button>

              {/* Clients Tab */}
              <button
                onClick={() => setSelectedTab('clients')}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'clients'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <Users className="h-4 w-4" />
                <span>Clients</span>
              </button>

              {/* Reports Tab */}
              <button
                onClick={() => setSelectedTab('reports')}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === 'reports'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <PieChart className="h-4 w-4" />
                <span>Reports</span>
              </button>

              {/* Configuration Dropdown - Right aligned, under New Enrollment button */}
              <div className="relative group ml-auto">
                <button
                  className="flex items-center space-x-2 py-4 px-1 border-b-2 border-transparent font-medium text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300"
                >
                  <Settings className="h-4 w-4" />
                  <span>Configuration</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="py-1">
                    <button
                      onClick={() => navigate('/cybersweep/audit-templates')}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FileText className="h-4 w-4" />
                      Audit Templates
                    </button>
                    <button
                      onClick={() => navigate('/cybersweep/website-configuration')}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <Globe className="h-4 w-4" />
                      Website Configuration
                    </button>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Overview Sub-toggle: My Work vs Team Dashboard */}
            <div className="flex items-center justify-between">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setOverviewMode('my-work')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    overviewMode === 'my-work'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  My Work
                </button>
                <button
                  type="button"
                  onClick={() => setOverviewMode('team-dashboard')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    overviewMode === 'team-dashboard'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Team
                </button>
              </div>
              {isManagerRole && (
                <button
                  onClick={handleViewManagerDashboard}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
                >
                  <BarChart3 className="h-4 w-4" />
                  Manager Dashboard
                </button>
              )}
            </div>

            {/* Overview Content */}
            {overviewMode === 'my-work' ? (
              <CyberSweepMyWork
                onViewAudit={(auditId) => {
                  setSelectedAudit(auditId);
                  setCurrentView('audit-detail');
                }}
                onBack={() => setSelectedTab('overview')}
              />
            ) : (
              <CyberSweepTeamDashboard
                onViewAudit={(auditId) => {
                  setSelectedAudit(auditId);
                  setCurrentView('audit-detail');
                }}
                onBack={() => setSelectedTab('overview')}
              />
            )}
          </div>
        )}

        {selectedTab === 'clients' && (
          <>
            {/* View Toggle for Clients tab */}
            <div className="flex items-center justify-end mb-6">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('board')}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'board'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4 mr-1.5" />
                  Board
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <List className="h-4 w-4 mr-1.5" />
                  List
                </button>
              </div>
            </div>

            {/* Main Toolbar: Search + Key Dropdowns + Filter Button */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-8">
          <div className="flex items-center gap-3">
            {/* Search - takes available space */}
            <div className="flex-1 min-w-[200px]">
              <StandardSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search clients..."
                size="sm"
              />
            </div>

            {/* Report Auditors - most used filter */}
            <div className="flex-shrink-0 w-[160px]">
              <StandardSelect
                value={selectedAuditor}
                onChange={(e) => setSelectedAuditor(e.target.value)}
                options={[
                  { value: '', label: 'All Auditors' },
                  ...auditorOptions
                ]}
                selectSize="sm"
              />
            </div>

            {/* Filters button - opens advanced filters panel */}
            <button
              onClick={() => setIsFilterPanelOpen(true)}
              className={`flex-shrink-0 relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                hasActiveFilters
                  ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold bg-blue-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Slide-in Filter Panel */}
        {isFilterPanelOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 transition-opacity"
              onClick={() => setIsFilterPanelOpen(false)}
            />
            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-[400px] bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-out overflow-y-auto">
              {/* Panel Header */}
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Advanced Filters</h2>
                <button
                  onClick={() => setIsFilterPanelOpen(false)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  Close
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="p-5 space-y-5">
                {/* Phase Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phase Status</label>
                  <StandardSelect
                    value={selectedPhase}
                    onChange={(e) => setSelectedPhase(e.target.value)}
                    options={[
                      { value: '', label: 'All Phases' },
                      { value: 'Not Started', label: 'Not Started' },
                      { value: 'Phase 1 - In Progress', label: 'Phase 1 - In Progress' },
                      { value: 'Phase 1 - Completed', label: 'Phase 1 - Completed' },
                      { value: 'Phase 1 - Road Block', label: 'Phase 1 - Road Block' },
                      { value: 'Phase 2 - In Progress', label: 'Phase 2 - In Progress' },
                      { value: 'Phase 2 - Completed', label: 'Phase 2 - Completed' },
                      { value: 'Phase 2 - Road Block', label: 'Phase 2 - Road Block' },
                      { value: 'Audit Complete', label: 'Audit Complete' },
                    ]}
                    selectSize="sm"
                  />
                </div>

                {/* Stages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Stages</label>
                  <StandardSelect
                    value={selectedStage}
                    onChange={(e) => setSelectedStage(e.target.value)}
                    options={[
                      { value: '', label: 'All Stages' },
                      ...cyberSweepStages.map(stage => ({ value: stage, label: stage }))
                    ]}
                    selectSize="sm"
                  />
                </div>

                {/* Phase 1 Date Range - only shown when Phase 1 is selected */}
                {showPhase1DateFilter && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                    <label className="block text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Phase 1 Date Range</label>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={phase1StartDate}
                          onChange={(e) => setPhase1StartDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">End Date</label>
                        <input
                          type="date"
                          value={phase1EndDate}
                          onChange={(e) => setPhase1EndDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Phase 2 Date Range - only shown when Phase 2 is selected */}
                {showPhase2DateFilter && (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
                    <label className="block text-sm font-medium text-purple-800 dark:text-purple-300 mb-2">Phase 2 Date Range</label>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-purple-600 dark:text-purple-400 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={phase2StartDate}
                          onChange={(e) => setPhase2StartDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-purple-600 dark:text-purple-400 mb-1">End Date</label>
                        <input
                          type="date"
                          value={phase2EndDate}
                          onChange={(e) => setPhase2EndDate(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quarters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Quarters</label>
                  <StandardSelect
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    options={[
                      { value: '', label: 'All Quarters' },
                      { value: 'Q1', label: 'Q1' },
                      { value: 'Q2', label: 'Q2' },
                      { value: 'Q3', label: 'Q3' },
                      { value: 'Q4', label: 'Q4' }
                    ]}
                    selectSize="sm"
                  />
                </div>

                {/* Report Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Report Due Date</label>
                  <input
                    type="date"
                    value={reportDueDate}
                    onChange={(e) => setReportDueDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Clear Filters Button */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedAuditor('');
                      setSelectedStage('');
                      setSelectedPhase('');
                      setSelectedQuarter('');
                      setPhase1StartDate('');
                      setPhase1EndDate('');
                      setPhase2StartDate('');
                      setPhase2EndDate('');
                      setReportDueDate('');
                    }}
                    disabled={!hasActiveFilters && !searchQuery}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      hasActiveFilters || searchQuery
                        ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    }`}
                  >
                    <X className="h-4 w-4" />
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Loading State */}
        {showClientsFullPageLoader && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading CyberSweep Clients...</p>
            </div>
          </div>
        )}

        {/* Empty State (list only; board columns show their own empty/loading states) */}
        {!showClientsFullPageLoader && displayRecords.length === 0 && viewMode !== 'board' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center mb-8">
            <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Client Records Found</h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              No CyberSweep Client records are available. Records will appear here once synced from the database.
            </p>
          </div>
        )}

        {/* Summary Stats — list: after first client page. Board: show immediately; numbers may show … until full list catches up (columns use their own queries). */}
        {showSummaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 min-h-[2rem] flex items-center justify-center">
                {totalClientsTilePending ? (
                  <Loader2 className="h-7 w-7 animate-spin text-gray-400" aria-label="Loading total client count" />
                ) : (
                  totalClientsForSummary
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Clients</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600 min-h-[2rem] flex items-center justify-center">
                {boardSummaryStatsPending ? (
                  <Loader2 className="h-7 w-7 animate-spin text-yellow-500/70" aria-hidden />
                ) : (
                  pendingEnrollments.length
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pending</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-red-600 min-h-[2rem] flex items-center justify-center">
                {roadblockedTilePending ? (
                  <Loader2 className="h-7 w-7 animate-spin text-red-500/70" aria-label="Loading roadblocked count" />
                ) : (
                  roadblockedCountForSummary
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Roadblocked</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 min-h-[2rem] flex items-center justify-center">
                {boardSummaryStatsPending ? (
                  <Loader2 className="h-7 w-7 animate-spin text-blue-500/70" aria-hidden />
                ) : (
                  awaitingAssignment.length + awaitingInitialReport.length + awaitingFinalReport.length
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">In Progress</div>
            </div>
          </div>
        )}

        {/* List View */}
        {!showClientsFullPageLoader && viewMode === 'list' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                All Clients
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                  {sortedAllClients.length}
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('clientName')}>
                      Client Name {getSortIcon('clientName')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('stage')}>
                      Stage {getSortIcon('stage')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('phaseStatus')}>
                      Phase Status {getSortIcon('phaseStatus')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('auditor')}>
                      Auditor {getSortIcon('auditor')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" onClick={() => handleSort('dueDate')}>
                      Due Date {getSortIcon('dueDate')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                  {sortedAllClients.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        No clients found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    sortedAllClients.map((client) => (
                      <tr
                        key={client.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        onClick={() => handleClientClick(client.clientName, client.auditId, client.sfId)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.clientName}</div>
                          <div className="text-xs text-gray-400">{client.auditId?.slice(0, 12)}{client.auditId?.length > 12 ? '...' : ''}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            client.stage === 'Pending Enrollments' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                            client.stage === 'Lacking Requirements' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                            client.stage === 'Awaiting Assignment' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                            client.stage === 'Pending Checklists' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                            client.stage?.includes('Awaiting') ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' :
                            client.stage?.includes('Quarterly') ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {client.stage}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {client.phaseStatus ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(client.phaseStatus)}`}>
                              {client.phaseStatus}
                            </span>
                          ) : <span className="text-sm text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {client.auditor || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {client.dueDate || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <StandardButton
                            variant="secondary"
                            size="sm"
                            leftIcon={<Eye className="h-3 w-3" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClientClick(client.clientName, client.auditId, client.sfId);
                            }}
                          >
                            View
                          </StandardButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {sortedAllClients.length > 0 && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-3">
                <span>
                  Showing {sortedAllClients.length}
                  {enrolledCountQuery.data != null ? ` of ${enrolledCountQuery.data}` : ''} client
                  {sortedAllClients.length !== 1 ? 's' : ''}
                  {infiniteClientsQuery.hasNextPage ? ' (loaded)' : ''}
                </span>
                {listUsesClientTraversalFields && listTraversalAuditorNamesQuery.isFetching && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">Resolving auditor names…</span>
                )}
                {!listUsesClientTraversalFields && csAuditsFetching && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">Updating audit columns…</span>
                )}
                {infiniteClientsQuery.hasNextPage && (
                  <StandardButton
                    variant="secondary"
                    size="sm"
                    onClick={() => infiniteClientsQuery.fetchNextPage()}
                    disabled={infiniteClientsQuery.isFetchingNextPage}
                  >
                    {infiniteClientsQuery.isFetchingNextPage ? 'Loading…' : 'Load more clients'}
                  </StandardButton>
                )}
              </div>
            )}
          </div>
        )}

        {/* Board View — horizontal scrollable kanban */}
        {viewMode === 'board' && (
          <div className="overflow-x-auto pb-4 -mx-4 px-4">
            {boardAnyColumnHydrating && (
              <div className="mb-3 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400" role="status">
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                Updating phase and audit details…
              </div>
            )}
            <div className="inline-flex gap-4 min-w-full">
              <CyberSweepBoardColumn
                colKey="pending"
                label="Pending Enrollments"
                icon={<Clock className="h-4 w-4 text-yellow-600" />}
                badgeCls="bg-yellow-100 text-yellow-800"
                cardCls="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-800/30"
                items={filteredBoardPendingEnrollments}
                hasNextPage={bcPendingEnrollments.hasNextPage}
                isFetchingNextPage={bcPendingEnrollments.isFetchingNextPage}
                isInitialLoading={bcPendingEnrollments.isPending}
                phaseDetailsLoading={bcPendingEnrollments.isHydratingBoardAudits}
                columnTotalCount={bcPendingEnrollments.columnTotalCount}
                columnTotalCountPending={bcPendingEnrollments.columnTotalCountPending}
                onLoadMore={() => bcPendingEnrollments.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="lacking"
                label="Lacking Requirements"
                icon={<AlertTriangle className="h-4 w-4 text-red-600" />}
                badgeCls="bg-red-100 text-red-800"
                cardCls="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-800/30"
                items={filteredBoardLackingRequirements}
                hasNextPage={bcLackingRequirements.hasNextPage}
                isFetchingNextPage={bcLackingRequirements.isFetchingNextPage}
                isInitialLoading={bcLackingRequirements.isPending}
                phaseDetailsLoading={bcLackingRequirements.isHydratingBoardAudits}
                columnTotalCount={bcLackingRequirements.columnTotalCount}
                columnTotalCountPending={bcLackingRequirements.columnTotalCountPending}
                onLoadMore={() => bcLackingRequirements.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="assignment"
                label="Awaiting Assignment"
                icon={<User className="h-4 w-4 text-blue-600" />}
                badgeCls="bg-blue-100 text-blue-800"
                cardCls="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-800/30"
                items={filteredBoardAwaitingAssignment}
                hasNextPage={bcAwaitingAssignment.hasNextPage}
                isFetchingNextPage={bcAwaitingAssignment.isFetchingNextPage}
                isInitialLoading={bcAwaitingAssignment.isPending}
                phaseDetailsLoading={bcAwaitingAssignment.isHydratingBoardAudits}
                columnTotalCount={bcAwaitingAssignment.columnTotalCount}
                columnTotalCountPending={bcAwaitingAssignment.columnTotalCountPending}
                onLoadMore={() => bcAwaitingAssignment.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="checklists"
                label="Pending Checklists"
                icon={<FileText className="h-4 w-4 text-purple-600" />}
                badgeCls="bg-purple-100 text-purple-800"
                cardCls="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-800/30"
                items={filteredBoardPendingChecklists}
                hasNextPage={bcPendingChecklists.hasNextPage}
                isFetchingNextPage={bcPendingChecklists.isFetchingNextPage}
                isInitialLoading={bcPendingChecklists.isPending}
                phaseDetailsLoading={bcPendingChecklists.isHydratingBoardAudits}
                columnTotalCount={bcPendingChecklists.columnTotalCount}
                columnTotalCountPending={bcPendingChecklists.columnTotalCountPending}
                onLoadMore={() => bcPendingChecklists.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="initial"
                label="Awaiting Initial Report"
                icon={<FileText className="h-4 w-4 text-indigo-600" />}
                badgeCls="bg-indigo-100 text-indigo-800"
                cardCls="bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-800/30"
                items={filteredBoardAwaitingInitialReport}
                hasNextPage={bcAwaitingInitialReport.hasNextPage}
                isFetchingNextPage={bcAwaitingInitialReport.isFetchingNextPage}
                isInitialLoading={bcAwaitingInitialReport.isPending}
                phaseDetailsLoading={bcAwaitingInitialReport.isHydratingBoardAudits}
                columnTotalCount={bcAwaitingInitialReport.columnTotalCount}
                columnTotalCountPending={bcAwaitingInitialReport.columnTotalCountPending}
                onLoadMore={() => bcAwaitingInitialReport.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="final"
                label="Awaiting Final Report"
                icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
                badgeCls="bg-green-100 text-green-800"
                cardCls="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-800/30"
                items={filteredBoardAwaitingFinalReport}
                hasNextPage={bcAwaitingFinalReport.hasNextPage}
                isFetchingNextPage={bcAwaitingFinalReport.isFetchingNextPage}
                isInitialLoading={bcAwaitingFinalReport.isPending}
                phaseDetailsLoading={bcAwaitingFinalReport.isHydratingBoardAudits}
                columnTotalCount={bcAwaitingFinalReport.columnTotalCount}
                columnTotalCountPending={bcAwaitingFinalReport.columnTotalCountPending}
                onLoadMore={() => bcAwaitingFinalReport.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="q1"
                label="Q1 Report"
                icon={<Calendar className="h-4 w-4 text-teal-600" />}
                badgeCls="bg-teal-100 text-teal-800"
                cardCls="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 hover:bg-teal-100 dark:hover:bg-teal-800/30"
                items={filteredBoardQuarterlyReport1}
                hasNextPage={bcQuarterlyReport1.hasNextPage}
                isFetchingNextPage={bcQuarterlyReport1.isFetchingNextPage}
                isInitialLoading={bcQuarterlyReport1.isPending}
                phaseDetailsLoading={bcQuarterlyReport1.isHydratingBoardAudits}
                columnTotalCount={bcQuarterlyReport1.columnTotalCount}
                columnTotalCountPending={bcQuarterlyReport1.columnTotalCountPending}
                onLoadMore={() => bcQuarterlyReport1.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="q2"
                label="Q2 Report"
                icon={<Calendar className="h-4 w-4 text-teal-600" />}
                badgeCls="bg-teal-100 text-teal-800"
                cardCls="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 hover:bg-teal-100 dark:hover:bg-teal-800/30"
                items={filteredBoardQuarterlyReport2}
                hasNextPage={bcQuarterlyReport2.hasNextPage}
                isFetchingNextPage={bcQuarterlyReport2.isFetchingNextPage}
                isInitialLoading={bcQuarterlyReport2.isPending}
                phaseDetailsLoading={bcQuarterlyReport2.isHydratingBoardAudits}
                columnTotalCount={bcQuarterlyReport2.columnTotalCount}
                columnTotalCountPending={bcQuarterlyReport2.columnTotalCountPending}
                onLoadMore={() => bcQuarterlyReport2.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="q3"
                label="Q3 Report"
                icon={<Calendar className="h-4 w-4 text-teal-600" />}
                badgeCls="bg-teal-100 text-teal-800"
                cardCls="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 hover:bg-teal-100 dark:hover:bg-teal-800/30"
                items={filteredBoardQuarterlyReport3}
                hasNextPage={bcQuarterlyReport3.hasNextPage}
                isFetchingNextPage={bcQuarterlyReport3.isFetchingNextPage}
                isInitialLoading={bcQuarterlyReport3.isPending}
                phaseDetailsLoading={bcQuarterlyReport3.isHydratingBoardAudits}
                columnTotalCount={bcQuarterlyReport3.columnTotalCount}
                columnTotalCountPending={bcQuarterlyReport3.columnTotalCountPending}
                onLoadMore={() => bcQuarterlyReport3.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
              <CyberSweepBoardColumn
                colKey="q4"
                label="Q4 Report"
                icon={<Calendar className="h-4 w-4 text-teal-600" />}
                badgeCls="bg-teal-100 text-teal-800"
                cardCls="bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 hover:bg-teal-100 dark:hover:bg-teal-800/30"
                items={filteredBoardQuarterlyReport4}
                hasNextPage={bcQuarterlyReport4.hasNextPage}
                isFetchingNextPage={bcQuarterlyReport4.isFetchingNextPage}
                isInitialLoading={bcQuarterlyReport4.isPending}
                phaseDetailsLoading={bcQuarterlyReport4.isHydratingBoardAudits}
                columnTotalCount={bcQuarterlyReport4.columnTotalCount}
                columnTotalCountPending={bcQuarterlyReport4.columnTotalCountPending}
                onLoadMore={() => bcQuarterlyReport4.fetchNextPage()}
                getPhaseStatusColor={getPhaseStatusColor}
                onClientClick={handleClientClick}
                onViewAudit={handleViewAudit}
              />
            </div>
          </div>
        )}
          </>
        )}

        {selectedTab === 'reports' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Meeting Agenda & Tracker */}
              <button
                onClick={() => {
                  console.log('[Reports] Clicking Meeting Agenda card, setting currentView to meeting-tracker');
                  setCurrentView('meeting-tracker');
                }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Meeting Agenda & Tracker</h4>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Weekly assignments, meeting agendas, and team directory
                </p>
              </button>

              {/* Placeholder for future reports */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-left border border-gray-200 dark:border-gray-700 opacity-50">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <PieChart className="h-5 w-5 text-gray-400" />
                  </div>
                  <h4 className="font-medium text-gray-500 dark:text-gray-400">Analytics Dashboard</h4>
                </div>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Coming soon
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <InternalChatbot
        applicationContext="cybersweep"
        position="bottom-right"
      />

      {/* New Enrollment Modal */}
      <NewCyberSweepEnrollmentModal
        isOpen={isNewEnrollmentModalOpen}
        onClose={() => setIsNewEnrollmentModalOpen(false)}
        onSuccess={(newClient) => {
          console.log('[CyberSweep] New enrollment created:', newClient);
          // Board will auto-refresh via query invalidation in the modal
        }}
      />
    </div>
  );
};

export default CyberSweepApp;
